// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PinInput } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Container, Stack, Text, Title } from '@/components/ui';
import { IconArrowLeft, IconDeviceMobile, IconBolt, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { NeonCard } from '../components';

export default function OtpDarkMode() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const theme = useMantineTheme();

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0f0a 0%, #0a1a1a 50%, #0a0f1a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: 250,
          height: 250,
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            <NeonCard color="multi" animated glass>
              {isVerified ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'rgba(0, 255, 102, 0.2)',
                        boxShadow: '0 0 30px rgba(0, 255, 102, 0.3)',
                      }}
                    >
                      <IconCheck size={40} color="#00ff66" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center" c="white">IDENTITY VERIFIED</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Authentication protocol completed successfully.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <Button
                      component={Link}
                      to="/demo/auth-pages/login/neon"
                      fullWidth
                      size="md"
                      radius="md"
                      color="cyan"
                      variant="filled"
                      style={{
                        boxShadow: '0 0 20px rgba(0, 255, 255, 0.4)',
                      }}
                    >
                      PROCEED TO SYSTEM
                    </Button>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/demo/auth-pages/otp"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                    c="cyan"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconBolt size={12} />}
                        variant="filled"
                        color="cyan"
                        size="lg"
                        style={{
                          boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
                        }}
                      >
                        Security Protocol
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'rgba(0, 255, 255, 0.2)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconDeviceMobile size={28} color="#00f5ff" />
                      </Center>
                      <Title order={2} ta="center" mt="sm" c="white">
                        VERIFY IDENTITY
                      </Title>
                      <Text size="sm" ta="center" c="dimmed" maw={300}>
                        Enter the 6-digit code transmitted to device **42
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="lg">
                      <FadeIn delay={300}>
                        <Center>
                          <PinInput
                            length={6}
                            size="lg"
                            value={otp}
                            onChange={handleChange}
                            type="number"
                            oneTimeCode
                            aria-label="Verification code"
                            styles={{
                              input: {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderColor: 'rgba(0, 255, 255, 0.3)',
                                color: '#00f5ff',
                                '&:focus': {
                                  borderColor: '#00f5ff',
                                  boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
                                },
                              },
                            }}
                          />
                        </Center>
                      </FadeIn>

                      <FadeIn delay={400}>
                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          color="cyan"
                          variant="filled"
                          disabled={!isComplete}
                          style={{
                            boxShadow: '0 0 20px rgba(0, 255, 255, 0.4)',
                          }}
                        >
                          AUTHENTICATE
                        </Button>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={500}>
                    <Text ta="center" size="sm" c="dimmed">
                      Code not received?{' '}
                      <Anchor component="button" type="button" c="cyan" fw={500}>
                        Retransmit
                      </Anchor>
                    </Text>
                  </FadeIn>

                  <FadeIn delay={600}>
                    <Center>
                      <Anchor component={Link} to="/demo/auth-pages/login/neon" size="sm" c="cyan">
                        <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Return to terminal
                      </Anchor>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </NeonCard>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
